<template>
  <div class="pane-sidebar d-flex flex-column">
    <div
      v-if="!$vuetify.display.mobile"
      class="pane-sidebar__header d-flex pa-4 column-gap-1 align-center flex-shrink-0">
      <app-icon-ai-assistant
        :width="28"
        :height="28"
        data-locator="pane-icon"
      />
      <span class="text--x-large font-weight-bold" data-locator="pane-title">
        {{ $t(`${TRANSLATION_KEY_PREFIX}.paneTitle`) }}
      </span>
    </div>

    <div class="mx-3 my-2">
      <div
        v-if="isConversationLimitReached"
        class="limit-popover-wrapper"
        @mouseenter="openLimitPopover"
        @mouseleave="scheduleLimitPopoverClose"
      >
        <app-button
          class="w-100 app-button--surface-brand"
          type="primary"
          data-locator="create-conversation-button"
          :disabled="isCreateConversationDisabled || isConversationLimitReached"
          @click="$emit('create-conversation')"
        >
          {{ $t(`${TRANSLATION_KEY_PREFIX}.createConversationButton`) }}
        </app-button>
        <div
          v-if="isLimitPopoverOpen"
          class="limit-popover pa-4"
        >
          <p class="limit-popover__title font-weight-bold mb-2">
            {{ $t(`${TRANSLATION_KEY_PREFIX}.limitReachedTitle`) }}
          </p>
          <p class="limit-popover__text text--small mb-4">
            {{ $t(`${TRANSLATION_KEY_PREFIX}.limitReachedText`) }}
          </p>
          <app-button
            type="primary"
            class="limit-popover__cta"
            data-locator="limit-popover-upgrade-button"
            @click="isUpgradeDialogOpen = true"
          >
            {{ $t(`${TRANSLATION_KEY_PREFIX}.limitReachedCta`) }}
          </app-button>
        </div>
      </div>

      <app-button
        v-else
        class="w-100 app-button--surface-brand"
        type="primary"
        data-locator="create-conversation-button"
        :disabled="isCreateConversationDisabled"
        @click="$emit('create-conversation')"
      >
        {{ $t(`${TRANSLATION_KEY_PREFIX}.createConversationButton`) }}
      </app-button>
    </div>

    <div class="pane-sidebar__content flex-grow-1 overflow-y-auto pt-2">
      <AIAssistantMenuItem
        data-locator="conversations-menu-item"
        :is-opened="conversationsMenuOpened"
        :title="$t(`${TRANSLATION_KEY_PREFIX}.conversationsMenuTitle`)"
        @toggle-menu-item="$emit('toggle-conversations-menu')"
      >
        <AIAssistantConversationsList @conversation-deleted="$emit('conversation-deleted', $event)" />
      </AIAssistantMenuItem>
    </div>

    <AIAssistantFreemiumBanner
      v-if="showFreemiumBanner"
      :remaining-conversations="remainingConversations"
      @upgrade="isUpgradeDialogOpen = true"
    />

    <div class="pane-sidebar__footer flex-shrink-0 border-t px-4 py-4">
      <nav class="d-flex flex-column">
        <app-button
          class="justify-start"
          data-locator="ai-assistant-help-button"
          type="ghost"
          @click="isHelpCenterModalOpen = true"
        >
          <span class="icon-wrapper">
            <app-material-icon name="help" />
          </span>
          <span>{{ $t(`${TRANSLATION_KEY_PREFIX}.helpButtonLabel`) }}</span>
        </app-button>
        <app-button
          class="justify-start"
          data-locator="ai-assistant-feedback-button"
          type="ghost"
        >
          <span class="icon-wrapper">
            <app-material-icon name="chat" :size="18" />
          </span>
          <span>{{ $t(`${TRANSLATION_KEY_PREFIX}.feedbackButtonLabel`) }}</span>
        </app-button>
      </nav>
    </div>

    <!-- MODALS -->
    <AIAssistantHelpCenterModal
      v-if="isHelpCenterModalOpen"
      @close="isHelpCenterModalOpen = false"
    />
    <AIAssistantUpgradeDialog
      v-if="isUpgradeDialogOpen"
      :conversation-limit="conversationLimit"
      @close="isUpgradeDialogOpen = false"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

import { DARK_BLUE_DARKEST } from '@/common/utils/colors'

import AIAssistantConversationsList from './AIAssistantConversationsList.vue'
import AIAssistantFreemiumBanner from './AIAssistantFreemiumBanner.vue'
import AIAssistantHelpCenterModal from './AIAssistantHelpCenterModal.vue'
import AIAssistantMenuItem from './AIAssistantMenuItem.vue'
import AIAssistantUpgradeDialog from './AIAssistantUpgradeDialog.vue'

const TRANSLATION_KEY_PREFIX = 'common.ai-assistant.AIAssistantPaneSidebar'

let limitPopoverCloseTimer: ReturnType<typeof setTimeout> | null = null

export default defineComponent({
  name: 'AIAssistantPaneSidebar',
  emits: ['conversation-deleted', 'create-conversation', 'toggle-conversations-menu'],
  components: {
    AIAssistantConversationsList,
    AIAssistantFreemiumBanner,
    AIAssistantHelpCenterModal,
    AIAssistantMenuItem,
    AIAssistantUpgradeDialog,
  },
  props: {
    conversationsMenuOpened: {
      type: Boolean,
      default: true,
    },
    isCreateConversationDisabled: {
      type: Boolean,
      default: false,
    },
    /** Whether freemium offer is active (not expired) */
    isFreemium: {
      type: Boolean,
      default: false,
    },
    /** Number of conversations remaining (null = no limit / not freemium) */
    remainingConversations: {
      type: Number as () => number | null,
      default: null,
    },
    /** Total conversation limit from subscription */
    conversationLimit: {
      type: Number as () => number | null,
      default: null,
    },
  },
  data() {
    return {
      DARK_BLUE_DARKEST,
      TRANSLATION_KEY_PREFIX,
      isHelpCenterModalOpen: false,
      isUpgradeDialogOpen: false,
      isLimitPopoverOpen: false,
    }
  },
  methods: {
    openLimitPopover() {
      if (limitPopoverCloseTimer) {
        clearTimeout(limitPopoverCloseTimer)
        limitPopoverCloseTimer = null
      }
      this.isLimitPopoverOpen = true
    },
    scheduleLimitPopoverClose() {
      limitPopoverCloseTimer = setTimeout(() => {
        this.isLimitPopoverOpen = false
        limitPopoverCloseTimer = null
      }, 100)
    },
    cancelLimitPopoverClose() {
      if (limitPopoverCloseTimer) {
        clearTimeout(limitPopoverCloseTimer)
        limitPopoverCloseTimer = null
      }
    },
  },
  computed: {
    showFreemiumBanner(): boolean {
      return this.isFreemium && this.remainingConversations !== null
    },
    isConversationLimitReached(): boolean {
      return this.isFreemium && this.remainingConversations !== null && this.remainingConversations <= 0
    },
  },
})
</script>

<style scoped lang="scss">
@import "@/scss/border-tokens";
@import "@/scss/box-shadow-tokens";

.pane-sidebar {
  height: 100%;
  max-height: 100%;
}

.pane-sidebar__content {
  min-height: 0;
}

.icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
}

.limit-popover-wrapper {
  position: relative;
}

.limit-popover {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  width: 280px;
  background: white;
  @include border-radius('border-radius-3');
  @include box-shadow('box-shadow-medium');
  border: get-border();

  &__title {
    color: #00172E;
    font-size: 14px;
    line-height: 20px;
  }

  &__text {
    color: #103051;
    line-height: 20px;
  }

  &__cta {
    background-color: #00172E !important;
    font-size: 14px !important;
    font-weight: 700 !important;
  }
}
</style>
